import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { callsStore } from "Stores/Calls";
import { userDetail } from "Stores/userDetail";
import dialerStoreData from "Stores/DialerData";
import CallsUtils from "Utils/CallsUtils";
import FilterByCardTab from "./FilterByCardTab";
import CallListItem from "./CallListItem";
import CallDetailPanel from "./CallDetailPanel/CallDetailPanel";
import EmptyReport from "Pages/Dashboard/reports/EmptyReport";
import NoActivityFound from "Pages/Dashboard/reports/NoActivityFound";
import PaginationComponent from "Components/common/PaginationComponent";
import PageLimit from "Components/common/PageLimit";
import CallsListLoader from "./CallsListLoader";
import CallDetailPanelLoader from "./CallDetailPanelLoader";
import { useLocation } from "react-router-dom";

const CallsOverview = observer(() => {
  const contactAdded = userDetail?.userFeatureFlag?.noOfContactsAddedInTeam || 0;
  const location = useLocation();

  const [selectedCallId, setSelectedCallId] = useState(null);
  const [search, setSearch] = useState("");

  const overviewData = callsStore.inputValues?.overviewData || {};
  const calls = overviewData?.calls || [];
  const overviewLoader = callsStore.inputValues?.overviewLoader;
  const filterLoader = callsStore.inputValues?.filterLoader;

  const filteredCalls = search
    ? calls.filter(c =>
        (c.contactName || "").toLowerCase().includes(search.toLowerCase())
      )
    : calls;

  const selectedCall =
    filteredCalls.find(c => c.id === selectedCallId) || filteredCalls[0] || null;

  useEffect(() => {
    setSelectedCallId(null);
  }, [calls]);

  useEffect(() => {
    return () => {
      callsStore.resetValue();
    };
  }, []);

  useEffect(() => {
    if (filteredCalls.length > 0 && !selectedCallId) {
      setSelectedCallId(filteredCalls[0]?.id || null);
    }
  }, [filteredCalls.length]);

  const filterAction = (value = "", callDispositionId = "") => {
    const activeTab = value?.toUpperCase() === "TOTAL" ? "" : value;
    const currentState = callsStore.inputValues;
    callsStore.setInputValues({
      ...currentState,
      currentTab: value,
      callDispositionId,
      overviewLoader: true,
      filterLoader: true
    });
    callsStore.setFilterPayload({
      callDispositionId: activeTab ? callDispositionId : null,
      pageNo: 1
    });
    const newObj = {
      ...callsStore.filterPayload,
      pageNo: 1,
      callDispositionId: activeTab ? callDispositionId : null
    };
    callsStore.setResetCheckbox(true);
    callsStore.getCallsOutcomeFilterData(CallsUtils.getPayload(newObj, true));
    callsStore.getCallsOverviewData(CallsUtils.getPayload(newObj));
  };

  const paginate = (pageNo = {}) => {
    callsStore.setInputValues({ overviewLoader: true, filterLoader: true });
    callsStore.setFilterPayload({ pageNo: pageNo?.selected + 1 });
    const newObj = { ...callsStore.filterPayload, pageNo: pageNo?.selected + 1 };
    callsStore.getCallsOutcomeFilterData(CallsUtils.getPayload(newObj, true));
    callsStore.getCallsOverviewData(CallsUtils.getPayload(newObj));
  };

  const handleLimitChange = (limit = 10) => {
    callsStore.setInputValues({ overviewLoader: true, filterLoader: true });
    callsStore.setFilterPayload({ limit, pageNo: 1 });
    const newObj = { ...callsStore.filterPayload, limit, pageNo: 1 };
    callsStore.getCallsOutcomeFilterData(CallsUtils.getPayload(newObj, true));
    callsStore.getCallsOverviewData(CallsUtils.getPayload(newObj));
  };

  const hasTableData = () =>
    !overviewLoader && contactAdded && calls.length > 0;

  return (
    <div className="callsOverview">
      <FilterByCardTab
        loader={filterLoader || false}
        filterCbk={filterAction}
        filterStats={callsStore?.inputValues?.filterObject || {}}
        filterList={[
          {
            id: "total",
            name: "TOTAL",
            displayName: "Total",
            displayEnabled: true
          },
          ...(dialerStoreData?.stageList || [])
        ]}
        currentTab={callsStore.inputValues?.currentTab}
        pageType="calls"
      />

      <div className="callsSplitPane">
        <div className="callsListPane">
          <div className="callsListPane__search">
            <i className="material-icons-outlined callsListPane__searchIcon">search</i>
            <input
              className="callsListPane__searchInput"
              placeholder="Search calls..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button
                className="callsListPane__clearSearch"
                onClick={() => setSearch("")}
              >
                <i className="material-icons-outlined">close</i>
              </button>
            )}
          </div>

          <div className="callsListPane__items">
            {overviewLoader && <CallsListLoader count={8} />}

            {!overviewLoader && !contactAdded && (
              <EmptyReport pathname={location?.pathname} />
            )}

            {!overviewLoader && contactAdded && calls.length === 0 && (
              <NoActivityFound />
            )}

            {hasTableData() &&
              filteredCalls.map(call => (
                <CallListItem
                  key={call.id}
                  call={call}
                  selected={call.id === (selectedCall?.id || null)}
                  onClick={() => setSelectedCallId(call.id)}
                />
              ))}

            {hasTableData() && filteredCalls.length === 0 && (
              <div className="callsListPane__noResults">No calls found.</div>
            )}
          </div>
        </div>

        <div className="callsDetailPane">
          {overviewLoader
            ? <CallDetailPanelLoader />
            : <CallDetailPanel call={selectedCall || {}} />
          }
        </div>
      </div>

      {hasTableData() && (
        <div className="callsPaginationFooter">
          <PaginationComponent
            callback={paginate}
            loading={overviewLoader}
            totalPages={overviewData?.totalPages || 0}
            currentPage={overviewData?.currentPage || 1}
          />
          <PageLimit
            limitPerPage={callsStore.filterPayload?.limit || 10}
            text="Calls per page"
            cbk={handleLimitChange}
            totalCount={overviewData?.totalCalls || 0}
          />
        </div>
      )}
    </div>
  );
});

export default CallsOverview;
export { CallsOverview };
